import { Asset, GSplatData, GSplatResource, Mat4, Vec3 } from 'playcanvas';

import { ElementType } from './element';
import { Mesh } from './mesh';
import type { Scene } from './scene';
import { Splat } from './splat';
import { State } from './splat-state';
import { cloneTypedArray } from './typed-array-utils';

// NOTE: Mesh sub-mesh splitting is called out as best-effort in the plan. We currently
// ship it as BLOCKED (explicitly) because reliable separation across glTF variants can
// require deep graph/material/buffer surgery.
export const splitMeshSubMeshes = (_mesh: Mesh, _scene: Scene): Mesh[] => {
    throw new Error('Split Sub-Meshes is currently BLOCKED: sub-mesh separation is not implemented yet');
};

const insertBeforeExtension = (filename: string, insert: string): string => {
    const input = String(filename ?? '');
    const sepIdx = Math.max(input.lastIndexOf('/'), input.lastIndexOf('\\'));
    const prefix = sepIdx >= 0 ? input.slice(0, sepIdx + 1) : '';
    const nameOnly = sepIdx >= 0 ? input.slice(sepIdx + 1) : input;

    const dotIdx = nameOnly.lastIndexOf('.');
    if (dotIdx <= 0) {
        return `${prefix}${nameOnly}${insert}`;
    }

    return `${prefix}${nameOnly.slice(0, dotIdx)}${insert}${nameOnly.slice(dotIdx)}`;
};

const getUniqueName = (scene: Scene, type: ElementType, desired: string): string => {
    const existing = new Set<string>();
    for (const el of scene.getElementsByType(type)) {
        const n = String((el as any).name ?? '').trim();
        if (n) existing.add(n);
    }

    if (!existing.has(desired)) return desired;

    for (let i = 2; i < 10_000; i++) {
        const candidate = `${desired} ${i}`;
        if (!existing.has(candidate)) return candidate;
    }

    return `${desired} ${Date.now()}`;
};

const getSelectedIndices = (splat: Splat): Uint32Array => {
    const state = splat.splatData.getProp('state') as Uint8Array;
    if (!state) return new Uint32Array(0);

    const out: number[] = [];
    for (let i = 0; i < state.length; i++) {
        if (state[i] === State.selected) out.push(i);
    }

    return new Uint32Array(out);
};

const subsetStorage = (storage: any, srcCount: number, selectedIndices: Uint32Array) => {
    const dstCount = selectedIndices.length;

    if (!storage || typeof storage !== 'object') {
        // Unknown storage type – preserve as-is (best effort). This is unlikely for GSplatData props.
        return storage;
    }

    // If it's a TypedArray (or looks like one).
    const hasBuffer = typeof (storage as any).buffer !== 'undefined' && typeof (storage as any).length === 'number';
    if (!hasBuffer) {
        // Best effort for rare storage types.
        if (typeof (storage as any).slice === 'function') {
            return (storage as any).slice(0);
        }
        return storage;
    }

    const src = storage as ArrayBufferView as any;
    const totalLen = Number(src.length);
    const stride = totalLen / srcCount;

    if (!Number.isFinite(stride) || stride <= 0 || Math.floor(stride) !== stride) {
        throw new Error(`splitSplatBySelection: non-integer stride for storage (len=${totalLen}, count=${srcCount})`);
    }

    const Ctor: any = (src as any).constructor;
    const dst = new Ctor(dstCount * stride);

    for (let j = 0; j < dstCount; j++) {
        const srcIdx = selectedIndices[j];
        const srcStart = srcIdx * stride;
        const dstStart = j * stride;
        for (let k = 0; k < stride; k++) {
            dst[dstStart + k] = src[srcStart + k];
        }
    }

    return dst;
};

const buildSplitGSplatData = (src: GSplatData, selectedIndices: Uint32Array): GSplatData => {
    const srcCount = src.numSplats;
    const dstCount = selectedIndices.length;

    const srcAny: any = src as any;
    const elements = (srcAny?.elements ?? srcAny?._elements ?? null) as any[] | null;

    const cloneElement = (el: any) => {
        const props = (el?.properties ?? []) as any[];
        return {
            name: String(el?.name ?? 'vertex'),
            count: Number(el?.count ?? srcCount),
            properties: props.map(p => ({
                type: p.type,
                name: p.name,
                storage: cloneTypedArray(p.storage),
                byteSize: p.byteSize
            }))
        };
    };

    const subsetElement = (el: any) => {
        const props = (el?.properties ?? []) as any[];
        return {
            name: String(el?.name ?? 'vertex'),
            count: dstCount,
            properties: props.map(p => ({
                type: p.type,
                name: p.name,
                storage: subsetStorage(p.storage, srcCount, selectedIndices),
                byteSize: p.byteSize
            }))
        };
    };

    if (Array.isArray(elements) && elements.length) {
        return new GSplatData(elements.map((el) => {
            // If the element is per-splat, subset it; otherwise preserve it.
            const count = Number(el?.count ?? srcCount);
            if (count === srcCount) {
                return subsetElement(el);
            }
            return cloneElement(el);
        }));
    }

    // Fallback: at least subset the vertex element.
    const vertex = (src as any).getElement?.('vertex');
    if (!vertex) {
        throw new Error('splitSplatBySelection: missing vertex element');
    }

    return new GSplatData([subsetElement(vertex)]);
};

const copySplitTransforms = (src: Splat, dst: Splat, selectedIndices: Uint32Array) => {
    // Preserve per-gaussian transform indices + palette, so previously-transformed splats stay transformed.
    // NOTE: These are stored on the Splat instance (textures/palette), not in GSplatData.

    const srcTex = src.transformTexture;
    const dstTex = dst.transformTexture;

    const lockedSrc = srcTex.lock() as Uint16Array;
    const srcIndices = new Uint16Array(lockedSrc.length);
    srcIndices.set(lockedSrc);
    srcTex.unlock();

    // Map palette indices used by selected gaussians.
    const used = new Set<number>();
    for (let i = 0; i < selectedIndices.length; i++) {
        used.add(srcIndices[selectedIndices[i]]);
    }

    const indexMap = new Map<number, number>();
    indexMap.set(0, 0);

    // Allocate + copy transforms for any non-zero palette entries.
    const mat = new Mat4();
    const sortedUsed = Array.from(used).filter(i => i !== 0).sort((a, b) => a - b);
    for (const oldIdx of sortedUsed) {
        if (indexMap.has(oldIdx)) continue;
        const newIdx = dst.transformPalette.alloc();
        src.transformPalette.getTransform(oldIdx, mat);
        dst.transformPalette.setTransform(newIdx, mat);
        indexMap.set(oldIdx, newIdx);
    }

    const lockedDst = dstTex.lock() as Uint16Array;
    lockedDst.fill(0);

    for (let j = 0; j < selectedIndices.length; j++) {
        const oldIdx = srcIndices[selectedIndices[j]];
        lockedDst[j] = indexMap.get(oldIdx) ?? 0;
    }

    dstTex.unlock();
};

const refreshSorterCenters = async (scene: Scene, splat: Splat) => {
    // The renderer's sorting uses `sorter.centers`, which are only updated for selected
    // splats in `Splat.updatePositions()`. A split splat is created with everything
    // unselected, so we refresh ALL centers once to keep sorting consistent.
    const sorter = splat?.contentEntity?.gsplat?.instance?.sorter;
    const centers = sorter?.centers as Float32Array | number[] | undefined;
    if (!centers) return;

    const data = await scene.dataProcessor.calcPositions(splat);
    const count = splat.splatData.numSplats;
    for (let i = 0; i < count; i++) {
        centers[i * 3 + 0] = data[i * 4 + 0];
        centers[i * 3 + 1] = data[i * 4 + 1];
        centers[i * 3 + 2] = data[i * 4 + 2];
    }
};

// Split a splat into two:
// - the original keeps all gaussians but the selected ones are expected to be marked deleted by the caller
// - the returned new splat contains ONLY the selected gaussians
//
// Returns null when selection is empty or contains all non-deleted gaussians.
export const splitSplatBySelection = async (splat: Splat, scene: Scene, selectedIndices?: Uint32Array): Promise<Splat | null> => {
    if (!splat || !scene) return null;

    const sel = selectedIndices ?? getSelectedIndices(splat);
    const selectedCount = sel.length;

    // splat.numSplats is non-deleted count (computed in updateState).
    const activeCount = splat.numSplats;

    if (selectedCount === 0) return null;
    if (activeCount > 0 && selectedCount >= activeCount) return null;

    const newData = buildSplitGSplatData(splat.splatData, sel);

    // New split splat should start unselected/undeleted/unlocked.
    const state = newData.getProp('state') as Uint8Array;
    if (state) {
        for (let i = 0; i < state.length; i++) {
            state[i] = 0;
        }
    }

    const srcFilename = String((splat.asset?.file as any)?.filename ?? splat.filename ?? splat.name ?? 'splat');
    const assetFilename = insertBeforeExtension(srcFilename, ' Split');

    const asset = new Asset(assetFilename, 'gsplat', { url: `split-gsplat-${Date.now()}`, filename: assetFilename });
    scene.app.assets.add(asset);
    asset.resource = new GSplatResource(scene.app.graphicsDevice, newData);

    const split = new Splat(asset, new Vec3(0, 0, 0));

    // Copy visible settings/appearance.
    split.name = getUniqueName(scene, ElementType.splat, `${splat.name} Split`);
    split.visible = splat.visible;

    // Copy pivot offset (C.4).
    try {
        split.pivotOffset.copy(splat.pivotOffset);
    } catch {
        // ignore
    }

    // Copy import normalization.
    try {
        split.setImportNormalization(splat.importNormalizationRotation);
    } catch {
        // ignore
    }

    // Copy color adjustments.
    split.tintClr = splat.tintClr;
    split.temperature = splat.temperature;
    split.saturation = splat.saturation;
    split.brightness = splat.brightness;
    split.blackPoint = splat.blackPoint;
    split.whitePoint = splat.whitePoint;
    split.transparency = splat.transparency;

    // Preserve per-gaussian transforms.
    try {
        copySplitTransforms(splat, split, sel);
    } catch (e) {
        console.warn('splitSplatBySelection: failed to copy per-splat transforms', e);
    }

    // Refresh sorting centers so split parts don't inherit stale pre-transform centers.
    try {
        await refreshSorterCenters(scene, split);
    } catch (e) {
        console.warn('splitSplatBySelection: failed to refresh sorter centers', e);
    }

    // Copy local TRS as a fallback (caller may override to preserve world exactly).
    try {
        split.move(
            splat.entity.getLocalPosition().clone(),
            splat.entity.getLocalRotation().clone(),
            splat.entity.getLocalScale().clone()
        );
    } catch {
        // ignore
    }

    return split;
};
